import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, Typography, IconButton, FormControl,
    InputLabel, Select, MenuItem, Chip, Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { PayrollService } from '../../services/payroll.service';
import { IEmployeeSalary, ISalaryComponent, IEmployeeSalaryCreate } from '../../interfaces/payroll.interface';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import { displayUsername } from '../../utils/userUtils';

interface Props {
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
    salary: IEmployeeSalary | null;
    components: ISalaryComponent[];
}

interface UserOption {
    id: string;
    username: string;
}

const EmployeeSalaryForm: React.FC<Props> = ({ open, onClose, onSaved, salary, components }) => {
    const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
    const [effectiveFrom, setEffectiveFrom] = useState('');
    const [details, setDetails] = useState<{ salaryComponentId: string; amount: number }[]>([]);
    const [users, setUsers] = useState<UserOption[]>([]);
    const [userInput, setUserInput] = useState('');
    const [userSearchLoading, setUserSearchLoading] = useState(false);
    const { t } = useTranslation(['payroll', 'common']);
    const { showApiError } = useToast();

    useEffect(() => {
        if (open) {
            if (salary) {
                const preFilled: UserOption = { id: salary.userId, username: salary.userName };
                setSelectedUser(preFilled);
                setUsers([preFilled]);
                setEffectiveFrom(salary.effectiveFrom);
                setDetails(salary.details.map(d => ({
                    salaryComponentId: d.salaryComponentId,
                    amount: d.amount
                })));
            } else {
                setSelectedUser(null);
                setUsers([]);
                setEffectiveFrom(new Date().toISOString().split('T')[0]);
                setDetails([]);
            }
            setUserInput('');
        }
    }, [open, salary]);

    useEffect(() => {
        if (!userInput.trim()) return;
        const handle = setTimeout(async () => {
            setUserSearchLoading(true);
            try {
                const data = await UserService.getInstitutionUsers(0, 20, undefined, userInput);
                setUsers(data.content || []);
            } catch (error) {
                showApiError(error, t('employeeSalaryForm.toast.searchFailed'));
            } finally {
                setUserSearchLoading(false);
            }
        }, 300);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInput]);

    const addDetail = () => {
        setDetails([...details, { salaryComponentId: '', amount: 0 }]);
    };

    const removeDetail = (index: number) => {
        setDetails(details.filter((_, i) => i !== index));
    };

    const updateDetail = (index: number, field: string, value: any) => {
        const updated = [...details];
        (updated[index] as any)[field] = value;
        setDetails(updated);
    };

    const handleSubmit = async () => {
        if (!selectedUser) return;
        const institutionId = AuthService.getSelectedInstitution()!!;
        const data: IEmployeeSalaryCreate = {
            institutionId,
            userId: selectedUser.id,
            effectiveFrom,
            details: details.filter(d => d.salaryComponentId && d.amount > 0)
        };

        try {
            if (salary) {
                await PayrollService.updateEmployeeSalary(salary.id, data);
            } else {
                await PayrollService.createEmployeeSalary(data);
            }
            onSaved();
        } catch (error) {
            showApiError(error, t('employeeSalaryForm.toast.saveFailed'));
        }
    };

    const activeComponents = components.filter(c => c.isActive);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{salary ? t('employeeSalaryForm.titleEdit') : t('employeeSalaryForm.titleAssign')}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', gap: 2, mb: 3, mt: 1 }}>
                    <Autocomplete
                        fullWidth
                        options={users}
                        getOptionLabel={(option) => displayUsername(option.username)}
                        value={selectedUser}
                        onChange={(_, newValue) => setSelectedUser(newValue)}
                        onInputChange={(_, value, reason) => {
                            if (reason === 'input') {
                                setUserInput(value);
                            } else if (reason === 'clear') {
                                setUserInput('');
                            }
                        }}
                        filterOptions={(x) => x}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        loading={userSearchLoading}
                        disabled={!!salary}
                        noOptionsText={userInput ? t('employeeSalaryForm.noEmployees') : t('employeeSalaryForm.typeToSearch')}
                        renderInput={(params) => (
                            <TextField {...params} label={t('shared.employee')} placeholder={t('employeeSalaryForm.searchPlaceholder')} />
                        )}
                    />
                    <TextField
                        label={t('employeeSalaryForm.effectiveFrom')}
                        type="date"
                        value={effectiveFrom}
                        onChange={(e) => setEffectiveFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1">{t('employeeSalaryForm.components')}</Typography>
                    <Button startIcon={<AddIcon />} onClick={addDetail} size="small">
                        {t('shared.addComponent')}
                    </Button>
                </Box>

                {details.map((detail, index) => {
                    const comp = activeComponents.find(c => c.id === detail.salaryComponentId);
                    return (
                        <Box key={index} sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
                            <FormControl sx={{ minWidth: 250 }}>
                                <InputLabel>{t('shared.component')}</InputLabel>
                                <Select
                                    value={detail.salaryComponentId}
                                    label={t('shared.component')}
                                    onChange={(e) => updateDetail(index, 'salaryComponentId', e.target.value)}
                                    size="small"
                                >
                                    {activeComponents.map(c => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.name}
                                            <Chip label={t(`componentType.${c.type}`)} size="small" sx={{ ml: 1 }}
                                                color={c.type === 'EARNING' ? 'success' : 'error'} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label={t('common:fields.amount')}
                                type="number"
                                value={detail.amount}
                                onChange={(e) => updateDetail(index, 'amount', parseFloat(e.target.value) || 0)}
                                size="small"
                                sx={{ width: 150 }}
                            />
                            {comp && (
                                <Chip label={t(`componentType.${comp.type}`)} size="small"
                                    color={comp.type === 'EARNING' ? 'success' : 'error'} />
                            )}
                            <IconButton onClick={() => removeDetail(index)} size="small" color="error" aria-label={t('employeeSalaryForm.removeComponent')}>
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    );
                })}
                {details.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t('employeeSalaryForm.empty')}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common:actions.cancel')}</Button>
                <Button onClick={handleSubmit} variant="contained"
                    disabled={!selectedUser || !effectiveFrom || details.length === 0}>
                    {t('common:actions.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmployeeSalaryForm;
